import styles from '../../LotteryGameRoomBodyWinners.module.scss';
import cx from 'classnames';

import { MoneyContainer } from '@shared/MoneyContainer';
import { Typography } from '@shared/Typography';
import { FC, memo } from 'react';
import { getMatchPrizeLabel } from './lib/helpers';
import { Jackpots, UserInfo, WinNumber } from './LotteryGameRoomBodyWinnerBase';
import { LotteryGameRoomBodyWinnerBaseProps } from './lib';
import { LotteryFrontJackpot, SuperTicketType } from '@features/LotteryGame/types';
import { getOrdinalNumberSuffix } from '@utils/getOrdinalNumberSuffix';
import {
  getTicketClassName,
  isMetallicTicket,
} from '@features/LotteryGame/helpers/ticketNumberUtils';

interface LotteryGameRoomBodyTopWinnerProps extends LotteryGameRoomBodyWinnerBaseProps {
  placesWithWinNumbers: {
    place: number;
    winNumber: number;
  }[];
  matchPrizeInfo?: {
    prize: string;
    orderedMatches: string;
    unorderedMatches: string;
  };
  ticketWin: number;
  jackpotsArray?: LotteryFrontJackpot[];
  luckyTicketNumber?: string;
  multiplier?: number;
}

export const LotteryGameRoomBodyTopWinner: FC<LotteryGameRoomBodyTopWinnerProps> = memo(
  ({
    user,
    avatar,
    userDisplayNumber,
    placesWithWinNumbers,
    matchPrizeInfo,
    ticketWin,
    jackpotsArray,
    winnerPlayer,
    luckyTicketNumber,
    multiplier,
  }) => {
    const ticketType = luckyTicketNumber as SuperTicketType;
    const ticketStyleClass = getTicketClassName(ticketType);
    return (
      <div className={styles.lgrbtwContainer}>
        {/* profile */}
        <div className={styles.lgrbtwUserWithPlaces}>
          {/* user avatar */}
          <div className={styles.lgrbtwUserInfo}>
            <UserInfo
              user={user}
              avatar={avatar}
              userDisplayNumber={userDisplayNumber}
              winnerPlayer={winnerPlayer}
            />
          </div>
          {/* places */}
          <div className={styles.lgrbtwWinNumbers}>
            {placesWithWinNumbers.map(({ place, winNumber }) => (
              <div className={styles.lgrbtwPlaces} key={winNumber}>
                <WinNumber place={1} winNumber={winNumber} />
                <Typography component="description2" variant={'secondary'}>
                  {place + getOrdinalNumberSuffix(place)}
                </Typography>
              </div>
            ))}
          </div>
        </div>
        {/* prize  */}
        <div className={cx(styles.lgrbwPrizeInfo, styles.lgrbtwPrizeInfo)}>
          <div className={styles.lgrbtwPrizesWrapper}>
            {matchPrizeInfo && (
              <div className={styles.lgrbtwPrize}>
                <Typography component="description2" variant={'primary'}>
                  {getMatchPrizeLabel(matchPrizeInfo)}
                </Typography>
                <MoneyContainer
                  prize={Number(matchPrizeInfo.prize).toFixed(2)}
                  variant={'dark'}
                />
              </div>
            )}
            <div className={styles.lgrbtwPrize}>
              {luckyTicketNumber ? (
                <div className={styles.lgrbtwLuckyTicketWrapper}>
                  <Typography component="description2" variant="primary">
                    {ticketType && isMetallicTicket(ticketType)
                      ? ticketType.includes('IRON')
                        ? 'Iron Ticket'
                        : ticketType.includes('BRONZE')
                        ? 'Bronze Ticket'
                        : 'Silver Ticket'
                      : 'Lucky Ticket'}
                  </Typography>

                  <div className={cx(styles.lgrbwLuckyTicket, styles[ticketStyleClass!])}>
                    <MoneyContainer
                      prize={ticketWin.toFixed(2)}
                      variant="dark"
                      className={cx(
                        styles.lgrbwLuckyTicketMoney,
                        styles[ticketStyleClass!],
                      )}
                      luckyTicketNumber={luckyTicketNumber}
                    />
                    <Typography
                      component="description"
                      className={cx(
                        styles.lgrbwLuckyTicketText,
                        styles[ticketStyleClass!],
                      )}
                    >
                      X {multiplier}
                    </Typography>
                  </div>
                </div>
              ) : (
                <>
                  <Typography component="description2" variant={'primary'}>
                    Ticket Win
                  </Typography>
                  <MoneyContainer prize={ticketWin.toFixed(2)} variant={'dark'} />
                </>
              )}
            </div>
          </div>
          <div className={cx(styles.lgrbwDivider, styles.lgrbtwDivider)} />
          <div className={styles.jackpotsWrapper}>
            {jackpotsArray?.map((jackpots) => {
              return <Jackpots jackpots={[jackpots]} key={jackpots.jackpotSlug} />;
            })}
          </div>
        </div>
      </div>
    );
  },
);
